import { z } from 'zod';
import { isoDateTime } from './common';

/**
 * emailCampaigns/{id} — one mass-email dispatch (event promotion).
 *
 * The admin app writes a doc with `status: 'queued'`; the
 * `onEmailCampaignCreate` Cloud Function picks it up, builds the audience
 * (inscriptions ∪ members − emailSuppressions), sends via Resend batch, and
 * writes progress back (`status`, counts, `sentAt`). We use a Firestore trigger
 * rather than a callable to sidestep this project's Cloud Run invoker fragility
 * (same reason `onMemberWrite` replaced the linking callable).
 *
 * Each dispatch is an immutable record: a "test" send and the subsequent "live"
 * send are two separate docs, giving a clean audit trail per event.
 */

/** 'test' → send only to `testEmail`. 'live' → send to the full audience. */
export const emailCampaignModeSchema = z.enum(['test', 'live']);
export type EmailCampaignMode = z.infer<typeof emailCampaignModeSchema>;

export const emailCampaignStatusSchema = z.enum(['queued', 'sending', 'sent', 'error']);
export type EmailCampaignStatus = z.infer<typeof emailCampaignStatusSchema>;

/** What the admin client writes to launch a dispatch. */
export const emailCampaignCreateSchema = z.object({
  /** Event being promoted (mutually exclusive with `formationId`). Drives the
   *  title, dates, CTA link and co-branding. */
  evenementId: z.string().optional(),
  /** Formation being promoted (mutually exclusive with `evenementId`). */
  formationId: z.string().optional(),
  subject: z.string().min(1).max(200),
  /** Free-text intro paragraph shown above the event recap. */
  introMessage: z.string().min(1).max(4000),
  mode: emailCampaignModeSchema,
  /** Required when `mode === 'test'`; ignored for 'live'. */
  testEmail: z.string().email().optional(),
  /** Cap this send at N recipients (installments). Undefined = no cap. */
  maxRecipients: z.number().int().positive().optional(),
  /** When true (default), exclude addresses already emailed for this event in
   *  prior live campaigns — so repeated sends cover only the remaining people. */
  onlyRemaining: z.boolean().optional(),
});
export type EmailCampaignCreate = z.infer<typeof emailCampaignCreateSchema>;

export const emailCampaignSchema = emailCampaignCreateSchema.extend({
  id: z.string(),
  status: emailCampaignStatusSchema,
  /** Admin uid/email who launched it (audit). */
  createdBy: z.string().nullable().default(null),
  createdByEmail: z.string().nullable().default(null),
  /** Filled by the function: how many unique addresses were targeted. */
  recipientsCount: z.number().int().nonnegative().default(0),
  sentCount: z.number().int().nonnegative().default(0),
  failedCount: z.number().int().nonnegative().default(0),
  /** Addresses successfully sent in THIS run — unioned across an event's live
   *  campaigns to compute who's already been contacted. */
  sentEmails: z.array(z.string()).default([]),
  /** Error message when `status === 'error'`. */
  error: z.string().nullable().default(null),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  sentAt: isoDateTime.nullable().default(null),
});
export type EmailCampaign = z.infer<typeof emailCampaignSchema>;
